import { Icon, type OptionProps } from '@amelio/ui-library';

import { EvaluationActions } from '@/constants/Actions';
import {
    EvaluationType,
    type EvaluationTypeValue,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import { PerformanceProFeature } from '@/constants/PerformanceProFeature';
import { useAmelioTranslations } from '@/hooks';
import { useProGate } from '@/hooks/performance-tier/useProGate';
import { ProGateOutcome } from '@/utils/performanceTier';
import { requestPerformanceUpgrade } from '@/utils/requestPerformanceUpgrade';

interface UseLaunchEvaluationOptionsParams {
    onNewReview: (evaluationType: EvaluationTypeValue) => void;
    isPending: boolean;
}

/**
 * The choices of "Launch an Evaluation". 360 Feedback and Self-Assessment are always offered.
 * Manager Assessment is Performance PRO: short of it, admins and HR see it locked, and choosing it
 * offers the upgrade; everyone else does not see it.
 */
export const useLaunchEvaluationOptions = ({
    onNewReview,
    isPending,
}: UseLaunchEvaluationOptionsParams): OptionProps[] => {
    const { t } = useAmelioTranslations();
    const managerAssessmentGate = useProGate();

    const options: OptionProps[] = [
        {
            id: EvaluationActions.CREATE_EVAL_360,
            label: t('cycleEvaluationDashboard.type.evaluation360'),
            onClick: () => onNewReview(EvaluationType.EVALUATION_360),
            disabled: isPending,
        },
        {
            id: EvaluationActions.CREATE_SELF_EVAL,
            label: t('cycleEvaluationDashboard.type.selfEvaluation'),
            onClick: () => onNewReview(EvaluationType.SELF_EVALUATION),
            disabled: isPending,
        },
    ];

    if (managerAssessmentGate === ProGateOutcome.Show) {
        options.push({
            id: EvaluationActions.CREATE_MANAGER_EVAL,
            label: t('cycleEvaluationDashboard.type.managerEvaluation'),
            onClick: () => onNewReview(EvaluationType.MANAGER_EVALUATION),
            disabled: isPending,
        });
    }

    if (managerAssessmentGate === ProGateOutcome.Lock) {
        options.push({
            id: EvaluationActions.CREATE_MANAGER_EVAL,
            label: t('cycleEvaluationDashboard.type.managerEvaluation'),
            icon: (
                <Icon
                    name={'security-lock'}
                    ariaLabel={t('performancePro.locked')}
                />
            ),
            onClick: () => requestPerformanceUpgrade(PerformanceProFeature.ManagerAssessment),
            dataTestId: 'launch-manager-assessment-locked',
        });
    }

    return options;
};
